import { useMemo, useState } from 'react';
import { useSnapshot } from 'valtio';
import store from '../store';
import { NO_CONFLICTS } from '.';
import { floodControlLayerIds, floodHazardLayerIds, hydrologyLayerIds } from './config';
import Table from './Table';
import Toggle from './Toggle';
import { useShouldOpen } from './useShouldOpen';

export default function HydrologyComponent() {
  const { config, projectIntelligence } = useSnapshot(store) as typeof store;
  const [_, setIsOpen] = useState(false);

  useShouldOpen({ layerIds: hydrologyLayerIds, setIsOpen });

  const getCount = (layerIds: string[]) => {
    let count = 0;
    layerIds.forEach((layerId) => {
      const feats = config[layerId];
      if (feats) {
        const length = Object.keys(feats).length;
        count += length;
      }
    });

    if (count === 0) {
      return NO_CONFLICTS;
    }

    return count;
  };

  // biome-ignore lint/correctness/useExhaustiveDependencies: legacy
  const count = useMemo(() => {
    const hydrology = getCount(hydrologyLayerIds);
    const fema = getCount(floodHazardLayerIds);
    const floodControl = getCount(floodControlLayerIds);

    return { hydrology, fema, floodControl };
  }, [config, projectIntelligence]);

  return (
    <Toggle label="Hydrology" featureCount={count.hydrology} layerIds={hydrologyLayerIds}>
      <div className="p-1">
        <Toggle size="small" label="FEMA / Flood Hazard Zones" layerIds={floodHazardLayerIds} featureCount={count.fema}>
          <Table
            size="small"
            label="Base Flood Elevations"
            columns={['Elevation']}
            rows={[
              {
                layerId: 'geotechnicalBaseFloodElevations',
                columnProperties: ['ELEV'],
              },
            ]}
            sortBy="ELEV"
            emptyMessage="No known base flood elevations in the project area"
          />
          <Table
            size="small"
            label="Coastal Transects"
            columns={['Water Body', 'Event Type', 'Beach Set']}
            rows={[
              {
                layerId: 'geotechnicalCoastalTransects',
                columnProperties: ['WTR_NM', 'EVENT_TYP', 'BEACH_SET'],
              },
            ]}
            sortBy="WTR_NM"
            emptyMessage="No known coastal transects in the project area."
          />
          <Table
            size="small"
            label="Cross Sections"
            columns={['Water Body']}
            rows={[
              {
                layerId: 'geotechnicalCrossSections',
                columnProperties: ['WTR_NM'],
              },
            ]}
            sortBy="WTR_NM"
            emptyMessage="No known cross sections in the project area."
          />
          <Table
            size="small"
            label="Flood Hazards"
            columns={['Zone Type', 'Flood Chance']}
            rows={[
              {
                layerId: 'geotechnicalFloodHazardZones',
                columnProperties: ['ZONE_SUBTY', 'FLD_CHANCE'],
              },
            ]}
            sortBy="ZONE_SUBTY"
            emptyMessage="No known flood hazards in the project area."
          />
        </Toggle>
        <Toggle label="Flood Control" layerIds={floodControlLayerIds} size="small" featureCount={count.floodControl}>
          <Table
            size="small"
            label="Levee System Points"
            columns={['Point Type', 'Crossing Type']}
            rows={[
              {
                layerId: 'geotechnicalLeveeSystemPoints',
                columnProperties: ['Type', 'CROSSING_TYPE'],
              },
            ]}
            sortBy="Type"
            emptyMessage="No known levee system points in the project area."
          />

          <Table
            size="small"
            label="Levee System Lines"
            columns={['Line Type', 'Flood Source']}
            rows={[
              {
                layerId: 'geotechnicalLeveeSystemLines',
                columnProperties: ['Type', 'FLOOD_SOURCE'],
              },
            ]}
            sortBy="Type"
            emptyMessage="No known levee system points in the project area."
          />

          <Table
            size="small"
            label="Levee Areas"
            columns={['Name']}
            rows={[
              {
                layerId: 'geotechnicalLeveeAreas',
                columnProperties: ['name'],
              },
            ]}
            sortBy="name"
            emptyMessage="No know levee areas in the project area."
          />

          <Table
            size="small"
            label="Dams"
            columns={['Name', 'Owner']}
            rows={[
              {
                layerId: 'otherJurisdictionalDams',
                columnProperties: ['Dam_Name', 'Owner_Name'],
              },
            ]}
            sortBy="Owner_Name"
            emptyMessage="No known dams in the project area."
          />
        </Toggle>
        <Table
          size="small"
          label="Waterbodies"
          columns={['Name']}
          rows={[
            { layerId: 'streams', columnProperties: ['GNIS_Name'] },
            { layerId: 'waterbodies', columnProperties: ['GNIS_Name'] },
            {
              layerId: 'environmentalWetlands',
              columnProperties: ['Organization'],
            },
            {
              layerId: 'environmentalDesignatedFloodway',
              columnProperties: ['Floodway_ID'],
            },
            {
              layerId: 'environmental303dImpairedWaters',
              columnProperties: ['wbid'],
            },
            {
              layerId: 'environmentalRegulatedStreams',
              columnProperties: ['GNIS_Name'],
            },
          ]}
          emptyMessage="No known waterbodies in the project area."
        />
      </div>
    </Toggle>
  );
}
